import { MouseEventHandler, useState, forwardRef, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { SparklesIcon, CircleStopIcon } from "lucide-react"
import { cn } from "@f2e/ui/lib/utils"
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
} from "@f2e/ui/components/base"

type ButtonProps = React.ComponentProps<typeof Button>

interface AIButtonProps {
  className?: string
  tooltip?: string
  loadingTooltip?: string
  withInstruction?: boolean
  isGenerating?: boolean
  onGenerate?: (instruction?: string) => void
  onCancel?: () => void
}

const AIButton = ({
  className,
  tooltip = "GenAI",
  loadingTooltip = "Cancel",
  withInstruction = false,
  isGenerating = false,
  onGenerate,
  onCancel,
}: AIButtonProps) => {
  const [isLoading, setIsLoading] = useState(isGenerating)
  const [isInstructionOpen, setIsInstructionOpen] = useState(false)

  useEffect(() => {
    setIsLoading(isGenerating)
  }, [isGenerating])

  const handleGenAI = (instruction?: string) => {
    setIsInstructionOpen(false)
    if (isLoading) {
      setIsLoading(false)
      onCancel?.()
    } else {
      setIsLoading(true)
      onGenerate?.(instruction)
    }
  }

  const handleInstructionPopoverKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    instructionText: string
  ) => {
    if (e.shiftKey && e.key === "Enter") {
      return
    }

    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleGenAI(instructionText)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {withInstruction && !isLoading ? (
            <InstructionPopover
              isOpen={isInstructionOpen}
              isLoading={isLoading}
              onOpenChange={setIsInstructionOpen}
              onSend={handleGenAI}
              onTextareaKeyDown={handleInstructionPopoverKeyDown}
              className={className}
            />
          ) : (
            <TriggerButton
              isLoading={isLoading}
              onClick={() => handleGenAI()}
              className={className}
            />
          )}
        </TooltipTrigger>
        <TooltipContent sideOffset={4} side="bottom">
          {isLoading ? <p>{loadingTooltip}</p> : <p>{tooltip}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface TriggerButtonProps extends ButtonProps {
  className?: string
  isLoading?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(
  ({ className, isLoading = false, onClick, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        className={cn("border-0 px-2 w-auto h-auto", className)}
        type="button"
        onClick={onClick}
        variant="ghost"
        {...props}
      >
        {isLoading ? (
          <CircleStopIcon className="w-4 h-4 animate-pulse" />
        ) : (
          <SparklesIcon className="w-4 h-4" />
        )}
      </Button>
    )
  }
)

interface InstructionPopoverProps extends ButtonProps {
  isOpen: boolean
  className?: string
  isLoading: boolean
  onOpenChange?: (isOpen: boolean) => void
  onSend?: (instructionText: string) => void
  onTextareaKeyDown?: (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
    instructionText: string
  ) => void
}

const InstructionPopover = forwardRef<
  HTMLButtonElement,
  InstructionPopoverProps
>(
  (
    {
      isOpen,
      className,
      isLoading,
      onOpenChange,
      onSend,
      onTextareaKeyDown,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation()

    const [instruction, setInstruction] = useState("")

    return (
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <TriggerButton
            ref={ref}
            isLoading={isLoading}
            className={className}
            {...props}
          />
        </PopoverTrigger>
        <PopoverContent className="w-64">
          <div className="space-y-2">
            <Textarea
              className="w-full"
              placeholder={t("common.aiButton.instruction.placeholder")}
              rows={1}
              value={instruction}
              disabled={isLoading}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => onTextareaKeyDown?.(e, instruction)}
            />
            <div className="flex justify-center">
              <Button
                size="sm"
                disabled={!instruction.trim() || isLoading}
                onClick={() => onSend?.(instruction)}
              >
                {t("common.aiButton.instruction.send")}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)

export { AIButton, InstructionPopover }
export default AIButton
